import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {QlService} from 'features/core/shared/ql/service/ql.service';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {ProvisionEventDeterminationType} from '../../enum/provision-event-determination-type.enum';

/**
 * Determination type of one provision event (K-15), keyed by the event id.
 *
 * `null` means "not determinable" — an event the lookup did not return, or a value the backend
 * delivered in a shape this mapper does not recognise. Callers must treat `null` as "unknown" and
 * NOT as `CONTRACT_START`, so an unreadable value can never silently hide the price-reason level.
 */
export type CpProvisionEventDeterminationTypeMap = { [eventId: number]: ProvisionEventDeterminationType | null };

/** One selectable commission component (K-14, UI term "Provisionsart"). */
export interface CpProvisionTypeComponentOption {
    id: number;
    name: string;
}

/**
 * Small QL lookups the ProvisionType object page needs BESIDE its widgets.
 *
 * Widgets fetch their own rows; these two lookups answer questions the widgets cannot, because the
 * answer decides how the page is BUILT rather than what a table contains:
 *
 *   1. {@link getEventDeterminationTypes} — 04-frontend.md §6 makes the price-reason level (4a)
 *      visible only for events of determination type `CONTRACT_PRICE_CHANGE`. The selectable of
 *      level 1 emits the selected id and does not promise to carry business attributes, so the
 *      types are loaded for all events of the provision type at once and the decision is available
 *      on the click itself instead of one round trip per selection.
 *   2. {@link getComponents} — 04 §6 asks for a pre-filter on the chosen commission component
 *      above the Matrix-Zuordnung table ("Vorfilter auf die gewählte Provisionsart als
 *      Bedien-Hilfe"). That select needs the components of exactly this provision type.
 *
 * ## Failure behaviour
 * Neither lookup may block the page. On error both return their empty result, and the callers are
 * built so that "unknown" degrades to MORE visible configuration, never to less: an unknown
 * determination type still shows the price-reason level, and an empty component list simply leaves
 * the pre-filter on "all". Errors are swallowed here on purpose (`showErrors(false)` +
 * `catchError`) — these are internal lookups, not user actions.
 */
@Injectable({providedIn: 'root'})
export class CpProvisionTypeLookupService {

    constructor(private qlService: QlService) {
    }

    /**
     * Load the determination type of every provision event (K-15) of one provision type.
     *
     * @param provisionTypeId the owning provision type
     * @returns event id -> determination type; an empty map when nothing could be loaded
     */
    getEventDeterminationTypes(provisionTypeId: number): Observable<CpProvisionEventDeterminationTypeMap> {

        if (!provisionTypeId) {
            return of({});
        }

        const queryName = 'cpContractEventDeterminationTypes';

        return this.qlService.builder()
            .showErrors(false)
            .query({
                name: queryName,
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'cp.ProvisionTypeContractEvent',
                    as: 'e',
                    fields: [
                        {name: 'id'},
                        {name: 'determinationType'}
                    ]
                },
                filters: [
                    FilterCriteria.create('e.provisionTypeId', FilterCriteria.cOperatorEqual, provisionTypeId)
                ]
            })
            .execute()
            .pipe(
                map(result => this.toDeterminationTypeMap(result.get(queryName))),
                catchError(() => of({} as CpProvisionEventDeterminationTypeMap))
            );
    }

    /**
     * Load the commission components (K-14, UI term "Provisionsart") of one provision type, for
     * the pre-filter above the Matrix-Zuordnung table.
     *
     * Sorted by `orderPriority` — the field that is unique per provision type (CR-023) and defines
     * the order of the reserve cap allocation, so the select mirrors the order in which the engine
     * processes the components.
     *
     * @param provisionTypeId the owning provision type
     * @returns id + display name per component; an empty list when nothing could be loaded
     */
    getComponents(provisionTypeId: number): Observable<CpProvisionTypeComponentOption[]> {

        if (!provisionTypeId) {
            return of([]);
        }

        const queryName = 'cpProvisionTypeComponents';

        return this.qlService.builder()
            .showErrors(false)
            .query({
                name: queryName,
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'cp.ProvisionTypeComponent',
                    as: 'e',
                    fields: [
                        {name: 'id'},
                        {name: 'name'},
                        {name: 'orderPriority'}
                    ]
                },
                filters: [
                    FilterCriteria.create('e.provisionTypeId', FilterCriteria.cOperatorEqual, provisionTypeId)
                ],
                sortings: [
                    new Sorting('e.orderPriority', true),
                    new Sorting('e.id', true)
                ]
            })
            .execute()
            .pipe(
                map(result => this.toComponentOptions(result.get(queryName))),
                catchError(() => of([] as CpProvisionTypeComponentOption[]))
            );
    }

    private toDeterminationTypeMap(rows: any[]): CpProvisionEventDeterminationTypeMap {

        const map: CpProvisionEventDeterminationTypeMap = {};

        for (const row of rows ?? []) {
            const id = row?.['e.id'];
            if (id === null || id === undefined) {
                continue;
            }
            map[+id] = this.toDeterminationType(row?.['e.determinationType']);
        }

        return map;
    }

    private toComponentOptions(rows: any[]): CpProvisionTypeComponentOption[] {

        const options: CpProvisionTypeComponentOption[] = [];

        for (const row of rows ?? []) {
            const id = row?.['e.id'];
            if (id === null || id === undefined) {
                continue;
            }
            const name = row?.['e.name'];
            options.push({
                id: +id,
                // A component without a name would render as an unselectable blank row; the id
                // keeps it addressable.
                name: name ? String(name) : ('Provisionsart ' + id)
            });
        }

        return options;
    }

    /**
     * Read the determination type tolerantly: the QL pipe delivers an `@Enumerated(ORDINAL)`
     * column either as the ordinal or as the enum constant name, depending on the form metadata
     * in play. Anything else yields `null` = unknown.
     */
    private toDeterminationType(value: any): ProvisionEventDeterminationType | null {

        if (value === null || value === undefined || value === '') {
            return null;
        }

        if (typeof value === 'number') {
            return ProvisionEventDeterminationType[value] !== undefined
                ? value as ProvisionEventDeterminationType
                : null;
        }

        if (typeof value === 'string') {
            const normalized = value.trim().toUpperCase();
            if (normalized === 'CONTRACT_START') {
                return ProvisionEventDeterminationType.CONTRACT_START;
            }
            if (normalized === 'CONTRACT_PRICE_CHANGE') {
                return ProvisionEventDeterminationType.CONTRACT_PRICE_CHANGE;
            }
            const numeric = Number(normalized);
            if (Number.isFinite(numeric) && ProvisionEventDeterminationType[numeric] !== undefined) {
                return numeric as ProvisionEventDeterminationType;
            }
        }

        return null;
    }

}
